import { lazy, Suspense, startTransition, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Clock, Server, Loader2, AlertTriangle, Trash2, History, Archive } from 'lucide-react';
import { useAudit } from '@/hooks/useAudit';
import { Loader } from '@/components/ui/loader';
import { AUDIT_RETENTION_DAYS, activeScopeOptions, canDeleteAudit, canViewModuleJson, fmtDate, hasScopeDetails, latestPurgeDate, statusBadgeVariant, STATUS_KEY, failedModuleKeys, rerunCommand } from '@/lib/utils';
import { labelClassName } from '@/components/ui/label';
import type { AuditDoc } from '@/types/models';
import { Button, buttonClassName } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { callDeleteAudit } from '@/lib/functions';
import { Dialog } from '@/components/ui/dialog';
import { BackLink } from '@/components/BackLink';
import { TabBar } from '@/components/TabBar';
import { AlertBanner } from '@/components/AlertBanner';
import { SummaryTab } from '@/components/audit/SummaryTab';
import { OverviewTab } from '@/components/audit/OverviewTab';
import { ModuleTab } from '@/components/audit/ModuleTab';
import { AdminReportsSection } from '@/components/audit/AdminReportsSection';
import { AuditHeaderBadges } from '@/components/audit/AuditHeaderBadges';
import { PairingBadge } from '@/components/audit/PairingStatus';
import { DataCatalogTab } from '@/components/audit/DataCatalogTab';
import { RecommendationsTab } from '@/components/audit/RecommendationsTab';
import { FleetTab, FleetCoverageBanner } from '@/components/audit/FleetTab';
import { EgressBanner, ReconciliationTab, useAuditQuestionnaire } from '@/components/audit/ReconciliationTab';

// Heavy (three.js / 3d-force-graph) — code-split so it only loads when the tab opens.
const FilesystemMapTab = lazy(() => import('@/components/audit/FilesystemMapTab').then(m => ({ default: m.FilesystemMapTab })));

/** Modules de collecte (01–06) rendus par ModuleTab ; 07 a son propre onglet cartographie. */
const MODULE_TABS = [
  '01_hardware_compute', '02_compute_platforms', '03_storage_access',
  '04_network', '05_data_sources', '06_orchestration',
] as const;

/**
 * Onglets du contrat 1.2, alimentés par deux artefacts hors module :
 * `json/data_catalog.json` (sidecar du module 08) et `json/recommendations.json`
 * (écrit par le pipeline). Sur un audit 1.0 / 1.1, aucun des deux documents
 * n'existe : l'onglet est masqué plutôt que rendu vide.
 */
const CATALOG_TAB = 'data_catalog';
const RECO_TAB = 'recommendations';

/**
 * Onglet « Parc » : inventaire distant du collecteur central (module 09,
 * `json/09_remote_inventory.json`). Absent d'un run local, qui n'inventorie que
 * sa propre machine — l'onglet n'est alors pas proposé.
 */
const FLEET_TAB = '09_remote_inventory';

/**
 * Onglet « Déclaré vs observé » : le questionnaire AI Readiness soumis de
 * l'organisation, confronté à ce que la collecte a trouvé (CPL-03/04). Le
 * questionnaire n'est lu que lorsque l'un des deux onglets qui s'en servent est
 * ouvert — celui-ci, et « Préconisations » pour le bandeau de sortie des données.
 */
const RECON_TAB = 'reconciliation';

export default function AuditDetail() {
  const { auditId } = useParams();
  const { t, locale } = useT();
  const { isGlobalAdmin, claims } = useAuth();
  const nav = useNavigate();
  const { audit, modules, loading } = useAudit(auditId);
  const [tab, setTab] = useState('summary');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if (!auditId) return;
    setDeleting(true);
    try {
      await callDeleteAudit({ auditId });
      toast.success(t('audit.delete.success'));
      nav('/dashboard');
    } catch (e) { toast.error((e as Error).message); setDeleting(false); }
  };

  const hasCatalog = !!modules[CATALOG_TAB];
  const hasReco = !!modules[RECO_TAB];
  const hasFleet = !!modules[FLEET_TAB];
  const needsQuestionnaire = tab === RECON_TAB || tab === RECO_TAB;
  const questionnaire = useAuditQuestionnaire(audit, needsQuestionnaire);

  const TABS = [
    { id: 'summary',              label: t('audit.tab.summary') },
    { id: 'overview',             label: t('audit.tab.overview') },
    ...MODULE_TABS.map(id => ({ id, label: t(`audit.tab.${id}`) })),
    { id: '07_filesystem_map',    label: t('audit.tab.07_filesystem_map') },
    ...(hasCatalog ? [{ id: CATALOG_TAB, label: t('audit.tab.data_catalog') }] : []),
    ...(hasReco ? [{ id: RECO_TAB, label: t('audit.tab.recommendations') }] : []),
    ...(hasFleet ? [{ id: FLEET_TAB, label: t('audit.tab.09_remote_inventory') }] : []),
    { id: RECON_TAB, label: t('audit.tab.reconciliation') },
    ...(isGlobalAdmin ? [{ id: 'admin_reports', label: t('reports.admin.section') }] : []),
  ];

  if (loading) return <Loader fullscreen label={t('common.loading')} />;
  if (!audit) return <Card>{t('audit.not_found')}</Card>;

  const canDelete = canDeleteAudit(claims.role, audit.status);
  const isProcessing = audit.status === 'uploading' || audit.status === 'processing';
  const failed = audit.status === 'ready' ? failedModuleKeys(modules) : [];
  const ndjsonPurgedAt = audit.retention?.ndjsonPurgedAt ?? null;
  const purgedAt = latestPurgeDate(audit.retention);
  // La cartographie 3D lit json/*.ndjson : org_admin et admin_global seulement (SEC-13).
  const canFsMap = canViewModuleJson(claims.role);
  // L'agrégat du module 07 vit dans Firestore : il SURVIT à la purge des NDJSON.
  // Masquer l'onglet parce que le détail a été purgé reviendrait à jeter la
  // synthèse chiffrée (répartition, tops, ancienneté, droits) qui, elle, reste.
  const hasFilesystemAggregate = !!modules['07_filesystem_summary']?.aggregate;
  const hasFilesystemData = hasFilesystemAggregate
    || (!ndjsonPurgedAt && (!!modules['07_filesystem_dirs'] || !!modules['07_filesystem_files']));
  const alwaysOn = new Set(['summary', 'overview', 'admin_reports', CATALOG_TAB, RECO_TAB,
    FLEET_TAB, RECON_TAB]);

  return (
    <div className="space-y-6">
      <BackLink to="/dashboard" label={t('audit.back')} />

      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h1 className="break-words font-display text-xl sm:text-2xl text-ink-900 dark:text-white">{audit.displayName}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-ink-500">
            <span className="inline-flex items-center gap-1"><Server className="h-3.5 w-3.5" /> {audit.systemName}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDate(audit.createdAt, locale)}</span>
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.7rem] dark:bg-ink-700">{audit.runId}</code>
            <Badge variant={statusBadgeVariant(audit.status)}>{t(STATUS_KEY[audit.status])}</Badge>
            {audit.totalElapsedSeconds != null && <span>{Math.round(audit.totalElapsedSeconds)}s</span>}
            <PairingBadge audit={audit} />
            <AuditHeaderBadges audit={audit} />
            <span className="inline-flex items-center gap-1" title={t('audit.retention.pending', { n: AUDIT_RETENTION_DAYS })}>
              <Archive className="h-3.5 w-3.5" />
              {purgedAt
                ? t('audit.retention.purged', { when: fmtDate(purgedAt, locale) })
                : t('audit.retention.pending', { n: AUDIT_RETENTION_DAYS })}
            </span>
          </div>
        </div>
        <div className="flex gap-2 self-start lg:self-auto">
          <Link to="/documents" className={buttonClassName('secondary')}>{t('audit.export')}</Link>
          {canDelete && (
            <Button
              variant="destructiveOutline"
              onClick={() => setDeleteOpen(true)}
              title={t('audit.delete')}
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">{t('audit.delete')}</span>
            </Button>
          )}
        </div>
      </header>

      {audit.status === 'failed' && (
        <AlertBanner variant="danger" icon={<AlertTriangle className="h-4 w-4" />} title={t('audit.failed.title')}>
          <div className="text-xs">{audit.error?.message ?? t('audit.failed.desc')}</div>
          {canDelete && <div className="mt-2 text-xs">{t('audit.delete.failed_only')}</div>}
        </AlertBanner>
      )}

      {isProcessing && (
        <AlertBanner variant="warning" icon={<Loader2 className="h-4 w-4 animate-spin" />} title={t('audit.processing.title')}>
          <div className="text-xs">{t('audit.processing.desc')}</div>
        </AlertBanner>
      )}

      {(failed.length > 0 || audit.partial || audit.interrupted) && audit.status === 'ready' && (
        <AlertBanner
          variant="warning"
          icon={<AlertTriangle className="h-4 w-4" />}
          title={audit.interrupted ? t('audit.partial.interrupted_title') : t('audit.partial.title')}
        >
          {failed.length > 0 && (
            <div className="text-xs">
              {t('audit.partial.desc', { modules: failed.join(', '), cmd: '' })}
              <code className="rounded bg-amber-100 px-1.5 py-0.5 dark:bg-amber-900/40">{rerunCommand(failed)}</code>
            </div>
          )}
        </AlertBanner>
      )}

      {audit.legacy && (
        <AlertBanner variant="info" icon={<History className="h-4 w-4" />} title={t('audit.legacy.title')}>
          <div className="text-xs">{t('audit.legacy.desc')}</div>
        </AlertBanner>
      )}

      {audit.status === 'ready' && <FleetCoverageBanner module={modules[FLEET_TAB]} />}

      <AuditScopeCard audit={audit} />

      <TabBar
        tabs={TABS.map(tabItem => ({
          id: tabItem.id,
          label: tabItem.label,
          disabled: !(
            alwaysOn.has(tabItem.id)
            || (tabItem.id === '07_filesystem_map' ? (hasFilesystemData || !!ndjsonPurgedAt) : !!modules[tabItem.id])
          ) && !isProcessing,
        }))}
        activeTab={tab}
        onChange={id => startTransition(() => setTab(id))}
      />

      <div>
        {tab === 'summary'          && <SummaryTab modules={modules} />}
        {tab === 'overview'         && <OverviewTab audit={audit} modules={modules} />}
        {tab === CATALOG_TAB        && <DataCatalogTab auditId={audit.id} module={modules[CATALOG_TAB]} schemaVersion={audit.schemaVersion} />}
        {tab === RECO_TAB           && (
          <div className="space-y-6">
            {/* La contrainte « les données ne peuvent pas sortir » décide de tout le reste : elle passe en tête. */}
            <EgressBanner declared={questionnaire.declared} />
            <RecommendationsTab auditId={audit.id} module={modules[RECO_TAB]} schemaVersion={audit.schemaVersion} />
          </div>
        )}
        {tab === FLEET_TAB          && <FleetTab auditId={audit.id} module={modules[FLEET_TAB]} schemaVersion={audit.schemaVersion} />}
        {tab === RECON_TAB          && (
          <ReconciliationTab
            audit={audit}
            modules={modules}
            declared={questionnaire.declared}
            loading={questionnaire.loading}
            loaded={questionnaire.loaded}
          />
        )}
        {tab === 'admin_reports'    && isGlobalAdmin && <AdminReportsSection audit={audit} />}
        {tab === '07_filesystem_map' && (!canFsMap ? (
          <AlertBanner variant="info" icon={<Archive className="h-4 w-4" />} title={t('audit.tab.07_filesystem_map')}>
            <div className="text-xs">{t('audit.fsmap.admins_only')}</div>
          </AlertBanner>
        ) : (
          <div className="space-y-4">
            {ndjsonPurgedAt && (
              <AlertBanner variant="info" icon={<Archive className="h-4 w-4" />} title={t('audit.tab.07_filesystem_map')}>
                <div className="text-xs">{t('audit.fsmap.purged', { when: fmtDate(ndjsonPurgedAt, locale) })}</div>
              </AlertBanner>
            )}
            {(!ndjsonPurgedAt || hasFilesystemAggregate) && (
              <Suspense fallback={<Loader label={t('common.loading')} />}>
                <FilesystemMapTab auditId={audit.id} modules={modules} detailPurged={!!ndjsonPurgedAt} />
              </Suspense>
            )}
          </div>
        ))}
        {!alwaysOn.has(tab) && tab !== '07_filesystem_map' && (
          <ModuleTab moduleKey={tab} module={modules[tab]} auditId={audit.id} retention={audit.retention} />
        )}
      </div>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        title={t('audit.delete.confirm.title')}
        description={t('audit.delete.confirm.desc')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={doDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {t('audit.delete.confirm.cta')}
            </Button>
          </>
        }
      >
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
          <strong>{audit.displayName}</strong>
          <div className="mt-0.5 text-xs">{audit.systemName} · <code>{audit.runId}</code></div>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * « Périmètre de l'audit » : configuration effective du run (contrat 1.1,
 * run_metadata.effective_config) et étiquettes. Rien n'est rendu pour un audit
 * 1.0 / legacy, qui ne porte aucune de ces clés.
 */
function AuditScopeCard({ audit }: { audit: AuditDoc }) {
  const { t } = useT();
  const cfg = audit.effectiveConfig ?? null;
  const tags = audit.tags && !Array.isArray(audit.tags)
    ? Object.entries(audit.tags).filter(([, v]) => typeof v === 'string')
    : [];
  // `run_metadata.collect` (1.2) : la trace de ce qui a été demandé au
  // collecteur central. Un run local n'en porte aucune, la ligne disparaît.
  const collect = audit.collect?.enabled === true ? audit.collect : null;
  if (!hasScopeDetails(cfg, audit.tags) && !collect) return null;

  const limits = [
    cfg?.maxMinutes != null ? t('audit.scope.max_minutes', { n: cfg.maxMinutes }) : null,
    cfg?.maxFiles != null ? t('audit.scope.max_files', { n: cfg.maxFiles.toLocaleString() }) : null,
    cfg?.maxDepth != null ? t('audit.scope.max_depth', { n: cfg.maxDepth }) : null,
  ].filter((x): x is string => !!x);
  const options = activeScopeOptions(cfg);

  return (
    <Card className="space-y-2 text-xs">
      <div className={labelClassName()}>{t('audit.scope.title')}</div>
      {cfg && (
        <ScopeRow label={t('audit.scope.roots')}>
          {cfg.scanRoots?.length
            ? cfg.scanRoots.map(r => <code key={r} className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-ink-700">{r}</code>)
            : <span className="text-ink-500">{t('audit.scope.roots.default')}</span>}
        </ScopeRow>
      )}
      {(cfg?.excludePaths?.length ?? 0) > 0 && (
        <ScopeRow label={t('audit.scope.excludes')}>
          {cfg!.excludePaths.map(x => <code key={x} className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-ink-700">{x}</code>)}
        </ScopeRow>
      )}
      {(cfg?.modules?.length ?? 0) > 0 && (
        <ScopeRow label={t('audit.scope.modules')}>
          {cfg!.modules!.map(m => <code key={m} className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-ink-700">{m}</code>)}
        </ScopeRow>
      )}
      {options.length > 0 && (
        <ScopeRow label={t('audit.scope.options')}>
          {options.map(opt => <Badge key={opt} variant="info">{t(`audit.scope.opt.${opt}` as const)}</Badge>)}
        </ScopeRow>
      )}
      {limits.length > 0 && (
        <ScopeRow label={t('audit.scope.limits')}>
          {limits.map(l => <Badge key={l} variant="neutral">{l}</Badge>)}
        </ScopeRow>
      )}
      {collect && (
        <ScopeRow label={t('audit.scope.collector')}>
          <Badge variant="info">{t('audit.scope.collector.on')}</Badge>
          {collect.hostsDeclared != null && (
            <Badge variant="neutral">{t('audit.scope.collector.hosts', { n: collect.hostsDeclared })}</Badge>
          )}
          {collect.directoryType && (
            <Badge variant="neutral">{t('audit.scope.collector.directory', { type: collect.directoryType })}</Badge>
          )}
        </ScopeRow>
      )}
      {tags.length > 0 && (
        <ScopeRow label={t('audit.scope.tags')}>
          {tags.map(([key, value]) => <Badge key={key} variant="neutral">{key}={value}</Badge>)}
        </ScopeRow>
      )}
    </Card>
  );
}

function ScopeRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-ink-500">{label}</span>
      {children}
    </div>
  );
}
